import React from 'react';
import { ZipcodeMetrics, MetricType } from './types';
import { formatMetricValue, getMetricValue, getMetricDisplayName } from './utils';

interface ZipcodeTooltipProps {
  zipcode: ZipcodeMetrics;
  selectedMetric: MetricType;
  position: { x: number; y: number };
  visible: boolean;
}

const ZipcodeTooltip: React.FC<ZipcodeTooltipProps> = ({
  zipcode,
  selectedMetric,
  position,
  visible,
}) => {
  if (!visible) return null;

  const primaryValue = getMetricValue(zipcode, selectedMetric);

  // Smart positioning to keep tooltip on screen
  const tooltipHeight = 200; // Approximate tooltip height
  const tooltipWidth = 250; // Approximate tooltip width
  const offset = 15;

  // Calculate optimal position
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  let finalX = position.x + offset;
  let finalY = position.y - tooltipHeight - offset;

  // Adjust if tooltip would go off right edge
  if (finalX + tooltipWidth > screenWidth) {
    finalX = position.x - tooltipWidth - offset;
  }

  // Adjust if tooltip would go off top edge
  if (finalY < 0) {
    finalY = position.y + offset;
  }

  // Adjust if tooltip would go off bottom edge
  if (finalY + tooltipHeight > screenHeight) {
    finalY = screenHeight - tooltipHeight - offset;
  }

  // Ensure tooltip doesn't go off left edge
  if (finalX < 0) {
    finalX = offset;
  }

  const allMetrics: { metric: MetricType; label: string; value: number }[] = [
    { metric: 'clicks', label: 'Clicks', value: zipcode.clicks },
    { metric: 'impressions', label: 'Impressions', value: zipcode.impressions },
    { metric: 'spend', label: 'Spend', value: zipcode.spend },
    { metric: 'conversions', label: 'Conversions', value: zipcode.conversions },
  ];

  // Add calculated metrics if they exist or can be calculated
  const ctr = zipcode.impressions > 0 ? (zipcode.clicks / zipcode.impressions) * 100 : 0;
  const cpc = zipcode.clicks > 0 ? zipcode.spend / zipcode.clicks : 0;
  const cpa = zipcode.conversions > 0 ? zipcode.spend / zipcode.conversions : 0;

  if (ctr > 0) allMetrics.push({ metric: 'ctr', label: 'CTR', value: ctr });
  if (cpc > 0) allMetrics.push({ metric: 'cpc', label: 'CPC', value: cpc });
  if (cpa > 0) allMetrics.push({ metric: 'cpa', label: 'CPA', value: cpa });

  return (
    <div
      className="fixed pointer-events-none zipcode-tooltip"
      style={{
        left: finalX,
        top: finalY,
        zIndex: 9999,
      }}
    >
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 min-w-[200px]">
        {/* Header */}
        <div className="border-b border-gray-100 pb-2 mb-2">
          <div className="font-semibold text-gray-900">
            Zipcode
            {' '}
            {zipcode.zipcode}
          </div>
          {zipcode.city && zipcode.state && (
            <div className="text-sm text-gray-600">
              {zipcode.city}
              ,
              {zipcode.state}
            </div>
          )}
        </div>

        {/* Primary metric highlight */}
        <div className="mb-3 p-2 bg-blue-50 rounded-md">
          <div className="text-sm text-gray-600">
            {getMetricDisplayName(selectedMetric)}
          </div>
          <div className="text-lg font-bold text-blue-600">
            {formatMetricValue(primaryValue, selectedMetric)}
          </div>
        </div>

        {/* All metrics */}
        <div className="space-y-1">
          {allMetrics.map(({ metric, label, value }) => (
            <div
              key={metric}
              className={`flex justify-between text-sm ${
                metric === selectedMetric ? 'font-medium text-blue-600' : 'text-gray-600'
              }`}
            >
              <span>
                {label}
                :
              </span>
              <span>
                {formatMetricValue(value, metric)}
              </span>
            </div>
          ))}
        </div>

        {/* Performance indicators */}
        {zipcode.clicks > 0 && zipcode.impressions > 0 && (
          <div className="mt-3 pt-2 border-t border-gray-100">
            <div className="text-xs text-gray-500">
              Performance Summary
            </div>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <div className="text-xs">
                <span className="text-gray-600">
                  CTR:
                  {' '}
                </span>
                <span className="font-medium">
                  {((zipcode.clicks / zipcode.impressions) * 100).toFixed(2)}
                  %
                </span>
              </div>
              {zipcode.conversions > 0 && (
                <div className="text-xs">
                  <span className="text-gray-600">
                    CVR:
                    {' '}
                  </span>
                  <span className="font-medium">
                    {((zipcode.conversions / zipcode.clicks) * 100).toFixed(2)}
                    %
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ZipcodeTooltip;
